/**
 * Mobile-only: adds a clear (×) control inside each Adyen `.adyen-checkout__input-wrapper`
 * under `#card-container` or `#ach-container`, matching AAA checkout visuals. Card number,
 * expiry, and security code are excluded.
 *
 * The × is shown only when the field contains a value.
 *
 * - Plain inputs (card holder, billing ZIP, ACH named inputs): clears value and dispatches events.
 * - Secured card iframes: Adyen does not expose a supported host-API to clear iframe contents; we focus the field
 *   (`setFocusOn`) so the shopper can delete with the keyboard. Plain fields above are fully cleared in place.
 */

const MOBILE_MQ = '(max-width: 768px)';
const BTN_CLASS = 'fp-adyen-mobile-clear';

export type AdyenClearContainerId = 'card-container' | 'ach-container';

const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(MOBILE_MQ).matches;
  } catch {
    return false;
  }
}

const clearPlainInput = (el: HTMLInputElement): void => {
  el.value = '';
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

const findAdyenFieldRoot = (wrapper: HTMLElement): HTMLElement | null => {
  return wrapper.closest('.adyen-checkout__field');
}

/** Map Adyen field container classes → Card `setFocusOn` / CSF fieldType strings. */
const inferCardSecuredFieldType = (fieldEl: HTMLElement | null): string | null => {
  if (!fieldEl) return null;
  const cl = fieldEl.className;
  if (cl.includes('adyen-checkout__field--cardNumber')) return 'encryptedCardNumber';
  if (cl.includes('adyen-checkout__field--securityCode')) return 'encryptedSecurityCode';
  if (cl.includes('adyen-checkout__field--encryptedExpiryMonth')) return 'encryptedExpiryMonth';
  if (cl.includes('adyen-checkout__field--encryptedExpiryYear')) return 'encryptedExpiryYear';
  if (cl.includes('adyen-checkout__field--expiryDate')) return 'encryptedExpiryDate';
  return null;
}

/** Card PAN, expiry, and CVV: no clear control (sensitive or iframe-only). */
const isExcludedCardPaymentField = (fieldRoot: HTMLElement | null): boolean => {
  if (!fieldRoot) return false;
  const cl = fieldRoot.className;
  return (
    cl.includes('adyen-checkout__field--cardNumber') ||
    cl.includes('adyen-checkout__field--securityCode') ||
    cl.includes('adyen-checkout__field--encryptedExpiryMonth') ||
    cl.includes('adyen-checkout__field--encryptedExpiryYear') ||
    cl.includes('adyen-checkout__field--expiryDate')
  );
}

const inferAchFieldName = (wrapper: HTMLElement): string | null => {
  const field = findAdyenFieldRoot(wrapper);
  const label = field?.querySelector<HTMLElement>('span.adyen-checkout__label__text[data-id]');
  const id = label?.dataset?.id;
  if (id && ['ownerName', 'routingNumber', 'accountNumber', 'accountNumberVerification'].includes(id)) {
    return id;
  }
  const input = wrapper.querySelector<HTMLInputElement>('input[name]');
  return input?.name ?? null;
}

/** True when this wrapper's field contains a non-empty value. */
const hasInputContent = (wrapper: HTMLElement): boolean => {
  const plain = wrapper.querySelector<HTMLInputElement>('input:not([type="hidden"])');
  if (plain) return plain.value.length > 0;
  // Secured Adyen iframes: the SDK marks the field root valid once filled
  const fieldRoot = findAdyenFieldRoot(wrapper);
  return (
    !!fieldRoot?.classList.contains('adyen-checkout__field--valid') ||
    !!wrapper.classList.contains('adyen-checkout__input--valid')
  );
}

const tryClearCardSecuredField = (cardComponent: unknown, securedType: string): void => {
  const c = cardComponent as { setFocusOn?: (field: string) => void } | null;
  if (typeof c?.setFocusOn === 'function') {
    c.setFocusOn(securedType);
  }
}

export interface InstallAdyenMobileClearOptions {
  containerId: AdyenClearContainerId;
  /** Adyen Card component instance (session Card). */
  cardComponent?: unknown;
}

export const installAdyenMobileClearButtons = (options: InstallAdyenMobileClearOptions): (() => void) => {
  const { containerId, cardComponent } = options;
    const container = () => document.getElementById(containerId);

    let mq: MediaQueryList | null = null;
    let onMq: (() => void) | null = null;
    let mo: MutationObserver | null = null;
    let rootFocusCleanup: (() => void) | null = null;

    const cleanupFns: Array<() => void> = [];

    const syncOneButton = (wrapper: HTMLElement, btn: HTMLButtonElement) => {
      const root = container();
      if (!root || !root.contains(wrapper)) return;

      const visible = hasInputContent(wrapper);

      btn.hidden = !visible;
      btn.setAttribute('aria-hidden', visible ? 'false' : 'true');
      wrapper.classList.toggle('fp-adyen-mobile-clearable', visible);
    };

    const onClearClick = (ev: Event) => {
      const btn = ev.currentTarget as HTMLButtonElement;
      const wrapper = btn.closest('.adyen-checkout__input-wrapper') as HTMLElement | null;
      const root = container();
      if (!wrapper || !root?.contains(wrapper)) return;

      ev.preventDefault();
      ev.stopPropagation();

      if (containerId === 'ach-container') {
        const name = inferAchFieldName(wrapper);
        const input = name ? (root.querySelector(`input[name="${name}"]`) as HTMLInputElement | null) : null;
        if (input) {
          clearPlainInput(input);
          input.focus();
        }
        syncOneButton(wrapper, btn);
        return;
      }

      const fieldRoot = findAdyenFieldRoot(wrapper);
      const plain = wrapper.querySelector<HTMLInputElement>('input:not([type="hidden"])');
      if (plain) {
        clearPlainInput(plain);
        plain.focus();
        syncOneButton(wrapper, btn);
        return;
      }

      const securedType = inferCardSecuredFieldType(fieldRoot);
      if (securedType && cardComponent) {
        tryClearCardSecuredField(cardComponent, securedType);
      }
      syncOneButton(wrapper, btn);
    };

    const ensureButtonOnWrapper = (wrapper: HTMLElement) => {
      if (wrapper.querySelector(`button.${BTN_CLASS}`)) return;

      if (containerId === 'card-container' && isExcludedCardPaymentField(findAdyenFieldRoot(wrapper))) {
        return;
      }

      const hasPlainInput = !!wrapper.querySelector('input:not([type="hidden"])');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = BTN_CLASS;
      btn.hidden = true;
      btn.setAttribute('aria-label', hasPlainInput || containerId === 'ach-container' ? 'Clear field' : 'Focus field');
      btn.innerHTML =
        '<span class="fp-adyen-mobile-clear__icon" aria-hidden="true">\u00d7</span>';
      btn.addEventListener('click', onClearClick);
      wrapper.appendChild(btn);

      const sync = () => syncOneButton(wrapper, btn);
      sync();

      const onFocusSync = () => {
        window.requestAnimationFrame(() => syncOneButton(wrapper, btn));
      };
      const onFocusInSync = () => syncOneButton(wrapper, btn);
      wrapper.addEventListener('focusin', onFocusInSync, true);
      wrapper.addEventListener('focusout', onFocusSync, true);
      wrapper.addEventListener('input', onFocusInSync, true);

      const fieldRoot = findAdyenFieldRoot(wrapper);
      let fieldMo: MutationObserver | null = null;
      if (fieldRoot) {
        fieldMo = new MutationObserver(() => syncOneButton(wrapper, btn));
        fieldMo.observe(fieldRoot, { attributes: true, attributeFilter: ['class'] });
        fieldMo.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
      }

      cleanupFns.push(() => {
        fieldMo?.disconnect();
        wrapper.removeEventListener('focusin', onFocusInSync, true);
        wrapper.removeEventListener('focusout', onFocusSync, true);
        wrapper.removeEventListener('input', onFocusInSync, true);
        btn.removeEventListener('click', onClearClick);
        btn.remove();
      });
    };

    const scan = () => {
      if (!isMobileViewport()) return;
      const root = container();
      if (!root) return;
      root.querySelectorAll<HTMLElement>('.adyen-checkout__input-wrapper').forEach(ensureButtonOnWrapper);
    };

    const start = () => {
      if (!isMobileViewport()) return;
      scan();

      const root = container();
      if (root && !mo) {
        mo = new MutationObserver(() => scan());
        mo.observe(root, { childList: true, subtree: true });
      }
      if (root && !rootFocusCleanup) {
        const syncAllImmediate = () => {
          const r = container();
          if (!r) return;
          r.querySelectorAll<HTMLButtonElement>(`button.${BTN_CLASS}`).forEach((btn) => {
            const w = btn.closest('.adyen-checkout__input-wrapper') as HTMLElement | null;
            if (w) syncOneButton(w, btn);
          });
        };
        const syncAllDeferred = () => {
          window.requestAnimationFrame(syncAllImmediate);
        };
        root.addEventListener('focusin', syncAllImmediate, true);
        root.addEventListener('focusout', syncAllDeferred, true);
        rootFocusCleanup = () => {
          root.removeEventListener('focusin', syncAllImmediate, true);
          root.removeEventListener('focusout', syncAllDeferred, true);
        };
      }
    };

    const stop = () => {
      if (mo) {
        mo.disconnect();
        mo = null;
      }
      rootFocusCleanup?.();
      rootFocusCleanup = null;
      cleanupFns.splice(0).forEach((fn) => fn());
    };

    const onViewportChange = () => {
      if (isMobileViewport()) {
        start();
      } else {
        stop();
      }
    };

    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      try {
        mq = window.matchMedia(MOBILE_MQ);
      } catch {
        mq = null;
      }
      if (mq) {
        onMq = () => onViewportChange();
        if ('addEventListener' in mq && typeof mq.addEventListener === 'function') {
          mq.addEventListener('change', onMq);
        } else if ('addListener' in mq && typeof (mq as MediaQueryList & { addListener?: (fn: () => void) => void }).addListener === 'function') {
          (mq as MediaQueryList & { addListener: (fn: () => void) => void }).addListener(onMq);
        }
        onViewportChange();
      }
    }

  return () => {
    stop();
    if (mq && onMq) {
      if ('removeEventListener' in mq && typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', onMq);
      } else if ('removeListener' in mq && typeof (mq as MediaQueryList & { removeListener?: (fn: () => void) => void }).removeListener === 'function') {
        (mq as MediaQueryList & { removeListener: (fn: () => void) => void }).removeListener(onMq);
      }
    }
  };
};
